import TextFieldComponent from '../../../components/TextFieldComponent';
import CheckboxComponent from '../../../components/CheckboxComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { Grid2 } from '@mui/material';
import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import zod from 'zod';
import { DeviceNetworkConfigFormSchema } from '../../../services/Devices/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetNetworkConfigAsync,
  useUpdateNetworkConfigAsync,
} from '../../../services/Devices/hooks/useMutation';
import { AppContext } from '../../../context/AppContextProvider';

interface DeviceNetworkConfigProps {
  id: number;
  setIsPendingChanges: Dispatch<SetStateAction<boolean>>;
  setFunctionToConfirmPendingChanges: Dispatch<SetStateAction<() => void>>;
}

type DeviceNetworkConfigFormInput = zod.infer<
  typeof DeviceNetworkConfigFormSchema
>;

const DeviceNetworkConfig = ({
  id,
  setIsPendingChanges,
  setFunctionToConfirmPendingChanges,
}: DeviceNetworkConfigProps) => {
  const { showSnackbar } = useContext(AppContext);
  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<DeviceNetworkConfigFormInput>({
    resolver: zodResolver(DeviceNetworkConfigFormSchema),
    defaultValues: {
      defaultGateway: '',
      ip: '',
      dnsServerPreference: '',
      hostname: '',
      isDhcp: false,
      subnetMask: '',
    },
  });

  const isWatchDhcp = watch('isDhcp');

  const {
    mutateAsync: mutateGetNetworkConfigAsync,
    isLoading: isLoadingGetNetworkConfigAsync,
  } = useGetNetworkConfigAsync();

  const {
    mutateAsync: mutateUpdateNetworkConfigAsync,
    isLoading: isLoadingUpdateNetworkConfigAsync,
  } = useUpdateNetworkConfigAsync();

  const handleSave = async (data: DeviceNetworkConfigFormInput) => {
    mutateUpdateNetworkConfigAsync(
      { id, data },
      {
        onSuccess: () => {
          showSnackbar(
            'Configuração de rede atualizada com sucesso.',
            'success',
          );
          setIsPendingChanges(false);
          reset();
        },
      },
    );
  };

  useEffect(() => {
    if (isDirty) {
      setIsPendingChanges(true);
      setFunctionToConfirmPendingChanges(() => handleSave);
    } else setIsPendingChanges(false);
  }, [isDirty]);

  useEffect(() => {
    if (id)
      mutateGetNetworkConfigAsync(id).then((v) =>
        reset(v, { keepDefaultValues: true }),
      );
  }, [id]);

  return (
    <>
      <Grid2 container columnSpacing={2}>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Host"
                errorMessage={error?.message}
                value={value}
                onChangeText={onChange}
              />
            )}
            name={'hostname'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Endereço IP"
                disabled={isWatchDhcp}
                errorMessage={error?.message}
                value={value}
                onChangeText={onChange}
              />
            )}
            name={'ip'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Máscara de rede"
                disabled={isWatchDhcp}
                errorMessage={error?.message}
                value={value}
                onChangeText={onChange}
              />
            )}
            name={'subnetMask'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Gateway padrão"
                disabled={isWatchDhcp}
                errorMessage={error?.message}
                value={value}
                onChangeText={onChange}
              />
            )}
            name={'defaultGateway'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }}>
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextFieldComponent
                label="Server DNS"
                value={value}
                errorMessage={error?.message}
                onChangeText={onChange}
              />
            )}
            name={'dnsServerPreference'}
            control={control}
          />
        </Grid2>
        <Grid2 size={{ xl: 6, xs: 12 }} paddingLeft={2}>
          <Controller
            render={({ field: { onChange, value } }) => (
              <CheckboxComponent
                label="DHCP"
                value={value}
                onChange={onChange}
              />
            )}
            name={'isDhcp'}
            control={control}
          />
        </Grid2>
        <Grid2 size={12} sx={{ mt: 1 }}>
          <ButtonComponent
            fullWidth
            onClick={handleSubmit(handleSave)}
            loading={
              isLoadingGetNetworkConfigAsync ||
              isLoadingUpdateNetworkConfigAsync
            }
          >
            Salvar
          </ButtonComponent>
        </Grid2>
      </Grid2>
    </>
  );
};

export default DeviceNetworkConfig;
